import React, { useEffect, useState } from "react";
import { Users, HardDrive, UserRound } from "lucide-react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const fmtBytes = (b) => {
  if (b == null) return "—";
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} ${tfr("Go")}`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} ${tfr("Mo")}`;
  return `${Math.max(1, Math.round(b / 1024))} ${tfr("Ko")}`;
};

const BAR_COLOR = { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-400" };

function Meter({ icon: Icon, label, used, limit, pct, warning, format, testid }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
        </p>
        <p className="text-xs tabular-nums">
          <span className="font-semibold text-foreground">{format(used)}</span>
          <span className="text-muted-foreground"> / {limit == null ? tfr("Illimité") : format(limit)}</span>
        </p>
      </div>
      {limit != null && (
        <div className="mt-2.5 h-1.5 rounded-full bg-secondary overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", BAR_COLOR[warning] || "bg-sky-500")}
            style={{ width: `${Math.min(100, pct || 0)}%` }}
          />
        </div>
      )}
      {warning && (
        <p className={cn("text-[11px] mt-1.5", warning === "critical" ? "text-red-400" : "text-amber-300/90")} data-testid={`${testid}-warning`}>
          {warning === "critical" ? tfr("Capacité atteinte — passez à une offre supérieure pour continuer d'ajouter.") : tfr("Vous approchez de la limite de votre offre.")}
        </p>
      )}
    </div>
  );
}

export default function UsageCard() {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    api.get("/billing/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);
  if (!usage) return null;
  return (
    <Card className="p-6 sm:p-8" data-testid="billing-usage-card">
      <h2 className="text-xl font-semibold tracking-tight">{tfr("Utilisation")}</h2>
      <p className="text-sm text-muted-foreground mt-1">{tfr("Capacités consommées sur votre offre actuelle.")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <Meter icon={Users} label={tfr("Joueurs actifs")} {...usage.players} format={(v) => String(v)} testid="usage-players" />
        <Meter icon={HardDrive} label={tfr("Stockage documents")} {...usage.storage} format={fmtBytes} testid="usage-storage" />
        <Meter icon={UserRound} label={tfr("Sièges utilisateurs")} {...usage.seats} format={(v) => String(v)} testid="usage-seats" />
      </div>
    </Card>
  );
}
